import type { ReadonlyURLSearchParams } from 'next/navigation';

// Allowlist of safe search parameter keys
const ALLOWED_SEARCH_PARAMS = [
  // Redirect URL
  'redirect_url',

  // Gift card redemption
  'gift_code',

  // Partner attribution
  'partner',

  // UTM Parameters (Standard)
  'utm_campaign',
  'utm_medium',
  'utm_source',
  'utm_term',
  'utm_content',
  'utm_referrer',
  'utm_id',

  // Referrer
  'referrer',

  // Portfolio attribution (public page → signup)
  'portfolio_code',
  'portfolio_url',
  // Which portfolio landing arm and which button sent the click (apps/portfolio). They carry the page
  // context on a paid click, whose own utm_content reaches sign-up unchanged.
  'portfolio_entry',
  'cta_location',

  // Homepage matcher hand-off (extern.com matcher → signup). Short codes from fixed lists and our own
  // program slugs, nothing personal (see sign-up/_utils/matcher-arrival.ts). Carried so the picked
  // program and the answers reach the account's events and survive the Google round trip.
  'handoff',
  'program',
  'picks',
  'focus',
  'goal',
  'stage',

  // Share-artifact attribution (a student's shared LinkedIn post → signup).
  // Carried in the URL rather than a cookie: the shared link points straight at
  // this app, so there is no cross-subdomain hop to bridge, and it avoids adding
  // a fourth root-domain tracking cookie while the consent classification in
  // docs/portfolio-attribution-contracts.md is still open.
  'share_code',

  // Google Ads & Analytics
  'gclid', // Google Ads Click ID
  'gclsrc', // Google Ads Source
  'dclid', // Display Click ID (Google Display Network)
  '_gl', // Google Analytics 4 client ID
  '_ga', // Google Analytics client ID
  '_ga_FQ0GRF3JQ5', // Google Analytics custom client ID
  'gbraid', // Google Ads Enhanced Conversions
  'wbraid', // Google Ads Web-to-App Attribution

  // Facebook & Instagram
  'fbclid', // Facebook Click ID
  'fb_action_ids', // Facebook Action IDs
  'fb_action_types', // Facebook Action Types
  'fb_source', // Facebook Source
  'fb_ref', // Facebook Referrer
  'igshid', // Instagram Share ID

  // Microsoft Advertising
  'msclkid', // Microsoft Click ID
  'msclkid_source', // Microsoft Click ID Source

  // TikTok
  'ttclid', // TikTok Click ID (already in the list)
  'ttp', // TikTok Pixel Cookie ID (_ttp) — injected by sync/page.tsx from cookie
  'tt_source', // TikTok Source
  'tt_campaign', // TikTok Campaign
  'tt_medium', // TikTok Medium
  'tt_content', // TikTok Content

  // LinkedIn
  'li_fat_id', // LinkedIn First-party Ad Tracking ID
  'li_geo', // LinkedIn Geographic Targeting
  'li_ocid', // LinkedIn One-Click Install ID
  'li_src', // LinkedIn Source

  // Twitter/X
  'twclid', // Twitter Click ID
  'tw_source', // Twitter Source
  'tw_campaign', // Twitter Campaign
  'tw_medium', // Twitter Medium
  'tw_content', // Twitter Content

  // Pinterest
  'epik', // Pinterest Enhanced Match
  'pin_campaign', // Pinterest Campaign
  'pin_source', // Pinterest Source
  'pin_medium', // Pinterest Medium

  // Snapchat
  'sccid', // Snapchat Click ID (current)
  'snap_click_id', // Snapchat Click ID
  'snap_source', // Snapchat Source
  'snap_campaign', // Snapchat Campaign

  // Reddit
  'rdt_cid', // Reddit Click ID
  'reddit_source', // Reddit Source
  'reddit_campaign', // Reddit Campaign
  'reddit_medium', // Reddit Medium

  // Quora
  'quora_source', // Quora Source
  'quora_campaign', // Quora Campaign

  // Taboola
  'taboola_source', // Taboola Source
  'taboola_campaign', // Taboola Campaign

  // Outbrain
  'ob_source', // Outbrain Source
  'ob_campaign', // Outbrain Campaign

  // Criteo
  'criteo_source', // Criteo Source
  'criteo_campaign', // Criteo Campaign

  // AdRoll
  'adroll_source', // AdRoll Source
  'adroll_campaign', // AdRoll Campaign

  // HubSpot
  'hs_source', // HubSpot Source
  'hs_campaign', // HubSpot Campaign
  'hs_medium', // HubSpot Medium
  'hs_content', // HubSpot Content

  // Mailchimp
  'mc_cid', // Mailchimp Campaign ID
  'mc_eid', // Mailchimp Email ID

  // ConvertKit
  'ck_subscriber_id', // ConvertKit Subscriber ID
  'ck_source', // ConvertKit Source

  // Klaviyo
  'klaviyo_source', // Klaviyo Source
  'klaviyo_campaign', // Klaviyo Campaign

  // ActiveCampaign
  'ac_source', // ActiveCampaign Source
  'ac_campaign', // ActiveCampaign Campaign

  // Drip
  'drip_source', // Drip Source
  'drip_campaign', // Drip Campaign

  // AWeber
  'aweber_source', // AWeber Source
  'aweber_campaign', // AWeber Campaign

  // GetResponse
  'gr_source', // GetResponse Source
  'gr_campaign', // GetResponse Campaign

  // Constant Contact
  'cc_source', // Constant Contact Source
  'cc_campaign', // Constant Contact Campaign

  // Campaign Monitor
  'cm_source', // Campaign Monitor Source
  'cm_campaign', // Campaign Monitor Campaign

  // SendGrid
  'sg_source', // SendGrid Source
  'sg_campaign', // SendGrid Campaign

  // Brevo (formerly Sendinblue)
  'brevo_source', // Brevo Source
  'brevo_campaign', // Brevo Campaign

  // Intercom
  'intercom_source', // Intercom Source
  'intercom_campaign', // Intercom Campaign

  // Drift
  'drift_source', // Drift Source
  'drift_campaign', // Drift Campaign

  // Calendly
  'calendly_source', // Calendly Source
  'calendly_campaign', // Calendly Campaign

  // Typeform
  'typeform_source', // Typeform Source
  'typeform_campaign', // Typeform Campaign

  // SurveyMonkey
  'surveymonkey_source', // SurveyMonkey Source
  'surveymonkey_campaign', // SurveyMonkey Campaign

  // Hotjar
  'hotjar_source', // Hotjar Source
  'hotjar_campaign', // Hotjar Campaign

  // FullStory
  'fullstory_source', // FullStory Source
  'fullstory_campaign', // FullStory Campaign

  // Mixpanel
  'mixpanel_source', // Mixpanel Source
  'mixpanel_campaign', // Mixpanel Campaign

  // Amplitude
  'amplitude_source', // Amplitude Source
  'amplitude_campaign', // Amplitude Campaign

  // Heap
  'heap_source', // Heap Source
  'heap_campaign', // Heap Campaign

  // Pendo
  'pendo_source', // Pendo Source
  'pendo_campaign', // Pendo Campaign

  // Gainsight
  'gainsight_source', // Gainsight Source
  'gainsight_campaign', // Gainsight Campaign

  // UserTesting
  'usertesting_source', // UserTesting Source
  'usertesting_campaign', // UserTesting Campaign

  // Optimizely
  'optimizely_source', // Optimizely Source
  'optimizely_campaign', // Optimizely Campaign

  // VWO
  'vwo_source', // VWO Source
  'vwo_campaign', // VWO Campaign

  // AB Tasty
  'abtasty_source', // AB Tasty Source
  'abtasty_campaign', // AB Tasty Campaign

  // Convert
  'convert_source', // Convert Source
  'convert_campaign', // Convert Campaign

  // Google Optimize
  'google_optimize_source', // Google Optimize Source
  'google_optimize_campaign', // Google Optimize Campaign

  // Adobe Target
  'adobe_target_source', // Adobe Target Source
  'adobe_target_campaign', // Adobe Target Campaign

  // Dynamic Yield
  'dynamicyield_source', // Dynamic Yield Source
  'dynamicyield_campaign', // Dynamic Yield Campaign

  // Monetate
  'monetate_source', // Monetate Source
  'monetate_campaign', // Monetate Campaign

  // Evergage
  'evergage_source', // Evergage Source
  'evergage_campaign', // Evergage Campaign

  // Segment
  'segment_source', // Segment Source
  'segment_campaign', // Segment Campaign

  // RudderStack
  'rudderstack_source', // RudderStack Source
  'rudderstack_campaign', // RudderStack Campaign

  // mParticle
  'mparticle_source', // mParticle Source
  'mparticle_campaign', // mParticle Campaign

  // Tealium
  'tealium_source', // Tealium Source
  'tealium_campaign', // Tealium Campaign

  // Google Tag Manager
  'gtm_source', // Google Tag Manager Source
  'gtm_campaign', // Google Tag Manager Campaign

  // Adobe Launch
  'adobe_launch_source', // Adobe Launch Source
  'adobe_launch_campaign', // Adobe Launch Campaign

  // Tealium iQ
  'tealium_iq_source', // Tealium iQ Source
  'tealium_iq_campaign', // Tealium iQ Campaign

  // Ensighten
  'ensighten_source', // Ensighten Source
  'ensighten_campaign', // Ensighten Campaign

  // Signal
  'signal_source', // Signal Source
  'signal_campaign', // Signal Campaign

  // Branch
  'branch_source', // Branch Source
  'branch_campaign', // Branch Campaign

  // AppsFlyer
  'appsflyer_source', // AppsFlyer Source
  'appsflyer_campaign', // AppsFlyer Campaign

  // Adjust
  'adjust_source', // Adjust Source
  'adjust_campaign', // Adjust Campaign

  // Kochava
  'kochava_source', // Kochava Source
  'kochava_campaign', // Kochava Campaign

  // Singular
  'singular_source', // Singular Source
  'singular_campaign', // Singular Campaign

  // Tenjin
  'tenjin_source', // Tenjin Source
  'tenjin_campaign', // Tenjin Campaign

  // Mobile Measurement Partner (MMP)
  'mmp_source', // MMP Source
  'mmp_campaign', // MMP Campaign

  // Custom/Generic tracking parameters
  'tracking_id', // Generic tracking ID
  'campaign_id', // Generic campaign ID
  'source_id', // Generic source ID
  'medium_id', // Generic medium ID
  'content_id', // Generic content ID
  'term_id', // Generic term ID
  'referrer_id', // Generic referrer ID
] as const;

/**
 * Filters search parameters to only include allowlisted keys and returns as a string.
 * This prevents persisting potentially sensitive information like PII or tokens.
 *
 * @param searchParams - The search parameters to filter
 * @returns A string representation of the filtered search parameters, or empty string if none
 */
export function getAllowlistedSearchParams(
  searchParams: ReadonlyURLSearchParams | null,
  fallback?: Record<string, string | undefined | null>,
): string {
  const filteredParams = new URLSearchParams();

  if (searchParams) {
    for (const [key, value] of searchParams.entries()) {
      if (ALLOWED_SEARCH_PARAMS.includes(key as (typeof ALLOWED_SEARCH_PARAMS)[number]) && value) {
        filteredParams.set(key, value);
      }
    }
  }

  if (fallback) {
    for (const [key, value] of Object.entries(fallback)) {
      if (
        ALLOWED_SEARCH_PARAMS.includes(key as (typeof ALLOWED_SEARCH_PARAMS)[number]) &&
        value &&
        !filteredParams.has(key)
      ) {
        filteredParams.set(key, value);
      }
    }
  }

  return filteredParams.toString();
}
